import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { WarningIcon } from "@phosphor-icons/react";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import type { TriageStatus } from "../SafetySupportScreen";
import { Severity } from "@axio/graphql/generated/graphql";

// CONFIG TYPES

export interface SeverityConfig {
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  rowBgColor: string;
}

export interface StatusConfig {
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

// CONFIG FUNCTIONS

export const getSeverityConfig = (severity: Severity): SeverityConfig => {
  switch (severity) {
    case Severity.Critical:
      return {
        label: "Critical",
        bgColor: colors.error[100],
        textColor: colors.error[700],
        borderColor: colors.error[200],
        rowBgColor: colors.error[50],
      };
    case Severity.High:
      return {
        label: "High",
        bgColor: colors.warning[100],
        textColor: colors.warning[700],
        borderColor: colors.warning[200],
        rowBgColor: colors.warning[25],
      };
    case Severity.Medium:
      return {
        label: "Medium",
        bgColor: colors.warning[50],
        textColor: colors.warning[700],
        borderColor: colors.warning[100],
        rowBgColor: colors.white,
      };
    case Severity.Low:
      return {
        label: "Low",
        bgColor: colors.blue[100],
        textColor: colors.blue[700],
        borderColor: colors.blue[100],
        rowBgColor: colors.white,
      };
  }
};

export const getStatusConfig = (status: TriageStatus): StatusConfig => {
  switch (status) {
    case "open":
      return {
        label: "Open",
        bgColor: colors.grey[100],
        textColor: colors.grey[600],
        borderColor: colors.grey[400],
      };
    case "in-progress":
      return {
        label: "In Progress",
        bgColor: colors.warning[50],
        textColor: colors.warning[700],
        borderColor: colors.warning[500],
      };
    case "resolved":
      return {
        label: "Resolved",
        bgColor: colors.success[100],
        textColor: colors.success[700],
        borderColor: colors.success[500],
      };
    case "escalated":
      return {
        label: "Escalated",
        bgColor: colors.purple[50],
        textColor: colors.purple[600],
        borderColor: colors.purple[500],
      };
  }
};

// COMPONENT

type TriageBadgeProps =
  | { variant: "severity"; value: Severity; label?: never }
  | { variant: "status"; value: TriageStatus; label?: string };

export function TriageBadge(props: TriageBadgeProps) {
  const { variant, value } = props;

  if (variant === "severity") {
    const config = getSeverityConfig(value);
    const showIcon = value === Severity.Critical;

    return (
      <View
        style={[
          styles.badge,
          styles.severityBadge,
          {
            backgroundColor: config.bgColor,
            borderColor: config.borderColor,
          },
        ]}>
        {showIcon && <WarningIcon size={12} color={config.textColor} style={styles.icon} />}
        <Text style={[styles.text, { color: config.textColor }]}>{config.label}</Text>
      </View>
    );
  }

  // variant === "status"
  const config = getStatusConfig(value);
  const displayLabel = props.label || config.label;

  return (
    <View
      style={[
        styles.badge,
        styles.statusBadge,
        {
          backgroundColor: config.bgColor,
          borderColor: config.borderColor,
        },
      ]}>
      <Text style={[styles.text, { color: config.textColor }]}>{displayLabel}</Text>
    </View>
  );
}

// CONVENIENCE WRAPPERS

interface SeverityBadgeProps {
  severity: Severity;
}

export function SeverityBadge({ severity }: SeverityBadgeProps) {
  return <TriageBadge variant="severity" value={severity} />;
}

interface StatusBadgeProps {
  status: TriageStatus;
  label?: string;
}

export function StatusBadge({ status, label }: StatusBadgeProps) {
  return <TriageBadge variant="status" value={status} label={label} />;
}

// STYLES

const styles = StyleSheet.create({
  badge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: radii.full,
  },
  severityBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xxs,
  },
  statusBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderWidth: 1,
  },
  icon: {
    marginRight: spacing.xxs,
  },
  text: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "400",
    padding: 2,
  },
});
